import React, {useEffect} from 'react'
import {useDispatch} from 'react-redux'
import styled from '@emotion/styled'
import {CountryType, MeasurementSystemType} from 'types/Localization'
import {WidgetLabelTypes} from 'utils/label'

import {SmoothScrollNavigation} from '@daedalus/atlas/experimental/SmoothScrollNavigation'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {useInView} from '@daedalus/core/src/utils/dom/hooks/useInView'

import {Hotel} from '../../../types/partnersApi/types'
import {HotelCard} from './HotelCard'
import {HotelCardsSkeleton} from './HotelCardsSkeleton'
import {LocationTitleSkeleton} from './LocationTitle'

interface Props {
  hotelsToShow: Hotel[]
  taxDisplayLogic: CountryType
  numberOfRooms: number
  currency: string
  partnerKey: string
  distanceUnit: MeasurementSystemType
  isRtl?: boolean
  label?: string
  searchCheckIn?: string
  searchCheckOut?: string
  searchDestination: string
  isDataLoaded?: boolean
  isSoldOut?: boolean
}

const HotelCarouselContainer = styled('div')``

const HotelCarouselSkeleton = () => (
  <>
    <LocationTitleSkeleton />
    <HotelCardsSkeleton />
  </>
)

export const HotelCarousel = ({
  isDataLoaded,
  isSoldOut,
  hotelsToShow,
  searchCheckIn,
  searchCheckOut,
  searchDestination,
  taxDisplayLogic,
  isRtl,
  numberOfRooms,
  currency,
  partnerKey,
  label,
  distanceUnit
}: Props) => {
  const dispatch = useDispatch()

  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})

  useEffect(() => {
    if (inView) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.Widget,
          action: Action.Viewed,
          payload: {
            partnerKey,
            widgetType: WidgetLabelTypes.multipleHotelsWithSearch,
            label
          }
        })
      )
    }
  }, [dispatch, inView, label, partnerKey])

  if (isDataLoaded && isSoldOut) return null

  const showCards =
    isDataLoaded &&
    !isSoldOut &&
    searchCheckIn &&
    searchCheckOut &&
    searchDestination

  return (
    <HotelCarouselContainer ref={ref}>
      {showCards ? (
        <>
          <SmoothScrollNavigation
            isRtlLanguage={isRtl}
            hideNavButtonsOnlyForMobileSm
          >
            {hotelsToShow.map((hotel, index) => (
              <HotelCard
                key={index}
                hotel={hotel}
                checkIn={searchCheckIn}
                checkOut={searchCheckOut}
                numberOfRooms={numberOfRooms}
                position={index + 1}
                taxDisplayLogic={taxDisplayLogic}
                currency={currency}
                partnerKey={partnerKey}
                label={label}
                distanceUnit={distanceUnit}
              />
            ))}
          </SmoothScrollNavigation>
        </>
      ) : (
        <HotelCarouselSkeleton />
      )}
    </HotelCarouselContainer>
  )
}
